'use client';
import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import { serverSearch } from './services/databaseService';
import ArticleDetail from './ArticleDetail';
import useTypingEffect from './hooks/useTypingEffect'; // Import the custom hook

function SearchPage() {
  const [inputText, setInputText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [similarArticles, setSimilarArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const articlesContainerRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const searchContainerRef = useRef(null);
  const [inputFocused, setInputFocused] = useState(false); // New state to track input focus
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false);
  const [showAboutPopup, setShowAboutPopup] = useState(false);

  // Example concepts to inspire the audience
  const conceptExamples = [
    "post-internet art",
    "ecological crisis",
    "artificial intelligence ethics",
    "decolonizing museums",
    "post-capitalism"
  ];

  // Use the custom hook for typing animation, pausing it when input is focused
  const animatedPlaceholder = useTypingEffect(
    conceptExamples, 
    80,        // typing speed 
    50,        // deleting speed
    2000,      // delay after typing
    500,       // delay after deletion
    inputFocused // Pass the focused state to pause animation when true
  );

  // Effect to handle custom scrolling animation
  useEffect(() => {
    if (shouldScroll && similarArticles.length > 0 && articlesContainerRef.current) {
      // Get the starting position and target position
      const startPosition = window.pageYOffset;
      const targetPosition = articlesContainerRef.current.getBoundingClientRect().top + window.pageYOffset;
      const stickyTriggerPoint = 350; // After scrolling this many pixels, make header sticky
      
      // Animation timing
      const totalDuration = 1500; // 1.5 seconds for the entire animation
      
      // Animation state
      let startTime = null;
      let stickyTriggered = false;
      
      function animateScroll(timestamp) {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / totalDuration, 1);
        
        // Easing function for smooth movement
        const easeInOutQuad = t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const easedProgress = easeInOutQuad(progress);
        
        // Calculate current scroll position
        const currentScrollY = startPosition + (targetPosition - startPosition) * easedProgress;
        
        // Check if we've scrolled past the threshold to make the header sticky
        if (!stickyTriggered && currentScrollY >= startPosition + stickyTriggerPoint) {
          setShowStickyHeader(true);
          stickyTriggered = true;
        }
        
        // Perform scroll
        window.scrollTo(0, currentScrollY);
        
        // Continue animation if not complete
        if (progress < 1) {
          window.requestAnimationFrame(animateScroll);
        }
      }
      
      // Begin scroll animation
      window.requestAnimationFrame(animateScroll);
      setShouldScroll(false);
    }
  }, [similarArticles, shouldScroll]);
  
  // Listen for scroll events to handle sticky header visibility when scrolling manually
  useEffect(() => {
    function handleScroll() {
      // Only apply this logic when we have search results
      if (similarArticles.length > 0) {
        const scrollPosition = window.scrollY;
        const mainHeaderHeight = 350; // Approximate height before we want sticky header
        
        setShowStickyHeader(scrollPosition >= mainHeaderHeight);
      }
    }
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [similarArticles.length]);

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  // New handlers for input focus and blur - fixed implementation
  const handleInputFocus = () => {
    // Immediately clear the input field on focus before any typing
    setInputText(''); 
    // Stop the animation
    setInputFocused(true);
  };

  const handleInputBlur = () => {
    // Only resume animation if input is empty
    if (!inputText.trim()) {
      setInputFocused(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;
    
    setLoading(true);
    setError(null);
    setSearchQuery(inputText);
    
    try {
      // Use server-side search (backend generates embeddings securely)
      const { articles } = await serverSearch(inputText);
      setSimilarArticles(articles);
      setShouldScroll(true); // Set flag to trigger scroll animation
      
    } catch (err) {
      setError(err.message || 'An error occurred during processing');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSearch = () => {
    setInputText('');
    setSearchQuery('');
    setSimilarArticles([]);
    setError(null);
    setShowStickyHeader(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Helper function to determine source from json_id
  const getSourceFromId = (id) => {
    return id.startsWith('kadist') ? 'kadist' : 'eflux';
  };

  const closePopup = () => {
    setShowPrivacyPopup(false);
    setShowAboutPopup(false);
  };

  return (
    <div className="App">
      {/* Main intro section - always visible */}
      <header className="App-header">
        <div className="intro-content">
          <h1 className="main-title">KADIST x e-flux</h1>
          
          <h2 className="subtitle">A new way to explore contemporary art and ideas</h2>
          
          <p className="description">
            <a 
              href="https://kadist.org" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ textDecoration: 'underline', color: 'inherit' }}
            >
              KADIST
            </a>&nbsp;and&nbsp;<a 
              href="https://e-flux.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ textDecoration: 'underline', color: 'inherit' }}
            >
              e-flux
            </a> have joined forces to create a unique digital platform that connects their archives 
            and collections through the power of artificial intelligence. At the core of this collaboration is a 
            tool that cross-references texts from the e-flux Journal with works from the KADIST collection, 
            offering users a dynamic and intuitive way to navigate contemporary art and critical discourse.
            Whether you're a curator, student, or curious visitor, the platform reveals unexpected connections 
            between ideas and images, theory and practice.
          </p>
          
          <div className="how-it-works-section">
            <h2 className="section-title">How it works</h2>
            <ol className="steps-list">
              <li>Type a concept, an idea, or a theme you'd like to investigate.</li>
              <li>Browse a curated selection of artworks and e-flux Journal articles.</li>
              <li>Click through to follow threads, discover new connections, and deepen your understanding.</li>
            </ol>
          </div>
        </div>
        
        {/* Search section - will become sticky based on showStickyHeader state */}
        <div 
          ref={searchContainerRef}
          className={showStickyHeader ? "sticky-header" : "search-container"}
        >
          {!showStickyHeader && (
            <h2 className="search-prompt">What do you want to explore today?</h2>
          )}
          
          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={inputText} // Controlled component with empty value on focus
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder={similarArticles.length > 0 ? 
                "Search another concept..." : 
                `${animatedPlaceholder}|`} // Add blinking cursor effect
              className="text-input"
            />
            {similarArticles.length > 0 ? (
              <button
                type="button"
                className="clear-search-button"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : (
              <button 
                type="submit" 
                disabled={loading}
                className="submit-button"
              >
                {loading ? 'Processing...' : 'Search Similar Articles'}
              </button>
            )}
          </form>
          
          {searchQuery && similarArticles.length > 0 && (
            <div className="search-query-display">
              Results for: <strong>{searchQuery}</strong>
            </div>
          )}
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {similarArticles.length > 0 && (
          <div 
            ref={articlesContainerRef} 
            className="articles-container"
          >
            <h3>Similar Articles</h3>
            <div className="articles-list">
              {similarArticles.map((article) => (
                <Link 
                  to={`/article/${getSourceFromId(article.json_id)}/${article.json_id}`}
                  className="card-link"
                  key={article.json_id}
                >
                  <div className="article-card">
                    {article.media && (
                      <div className="article-card-media">
                        <img src={article.media} alt={article.title} />
                      </div>
                    )}
                    <h4>{article.title}</h4>
                    <p>{article.summary}</p>
                    <div className="article-tags">
                      {article.tags.map(tag => (
                        <span key={tag} className="article-tag">{tag}</span>
                      ))}
                    </div>
                    <div className="article-footer">
                      <p className="article-source">Source: {getSourceFromId(article.json_id) === 'kadist' ? 'KADIST' : 'e-flux'}</p>
                      <span className="read-button">Read</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
        
        {/* Footer with partner logos */}
        <footer className="app-footer">
          <div className="footer-content">
            <div className="footer-links-left">
              <button 
                className="footer-link" 
                onClick={() => setShowPrivacyPopup(true)}
              >
                Privacy
              </button>
              <button 
                className="footer-link" 
                onClick={() => setShowAboutPopup(true)}
              >
                About
              </button>
            </div>
            <div className="footer-logos">
              <a href="https://kadist.org" target="_blank" rel="noopener noreferrer" className="logo-link">
                <img src="/kadist.png" alt="KADIST Logo" className="footer-logo" />
              </a>
              <a href="https://e-flux.com" target="_blank" rel="noopener noreferrer" className="logo-link">
                <img src="/e-flux.png" alt="e-flux Logo" className="footer-logo" />
              </a>
            </div>
          </div>
        </footer>

        {/* Privacy Popup */}
        {showPrivacyPopup && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
              <h2>Privacy Policy</h2>
              <p>
                This is a placeholder for the privacy policy. Here you can describe how user data is collected, stored, and used.
              </p>
              <button className="close-button" onClick={closePopup}>Close</button>
            </div>
          </div>
        )}

        {/* About Popup */}
        {showAboutPopup && (
          <div className="popup-overlay" onClick={closePopup}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
              <h2>About</h2>
              <p>
                This is a placeholder for the about section. Here you can provide information about the project, its purpose, and its creators.
              </p>
              <button className="close-button" onClick={closePopup}>Close</button>
            </div>
          </div>
        )}
      </header>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<SearchPage />} />
        <Route path="/article/:source/:id" element={<ArticleDetail />} />
      </Routes>
    </Router>
  );
}

export default App;
